/**
 * Шапка приложения: кто ты, как у тебя дела и куда можно пойти.
 *
 * Слева — аватар квокки с приветствием и ступенью CEFR (уровень читается
 * под именем, а не отдельной цифрой где-то дальше), по центру — верхние
 * табы, справа — счётчики и звук.
 *
 * Верхние табы живут только на широком экране: на узком их роль берёт
 * нижний таб-бар. Это переключение — по размеру, а не по состоянию, —
 * поэтому в шапке нет бургера и логики «открыто/закрыто».
 */

import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';

import { NAV_ITEMS } from '../../app/navigation';
import { useProfile } from '../../hooks/useProfile';
import { SoundToggle } from '../SoundToggle';
import { ThemeToggle } from '../ThemeToggle';
import { Logo } from './Logo';
import { StatusChips } from './StatusChips';
import { levelName } from '../../content/levels';
import { useTranslation } from 'react-i18next';

/** Общий вид пилюли-таба: активная приподнята и очерчена, покой — плоский. */
function tabClass(active: boolean): string {
  return [
    'relative inline-flex h-9 items-center gap-2 rounded-full px-4',
    'font-heading text-sm transition-colors duration-200',
    active ? 'font-semibold text-heading' : 'text-muted hover:text-heading',
  ].join(' ');
}

export function AppHeader() {
  const profile = useProfile();
  const { t } = useTranslation();

  return (
    <header className="glass sticky top-0 z-30 flex h-16 w-full items-center gap-3 px-4 shadow-[0_1px_0_rgb(var(--c-heading)/0.06),0_8px_24px_-16px_rgb(var(--c-heading)/0.35)] sm:gap-4 sm:px-6 lg:px-8">

      {/* Левая группа: логотип и приветствие. `min-w-0` + `truncate` —
          чтобы длинное приветствие сжималось, а не выдавливало
          навигацию и счётчики. */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Logo />
        <span
          aria-hidden="true"
          className="hidden h-7 w-px shrink-0 bg-line lg:block"
        />

        <div className="hidden min-w-0 flex-col xl:flex">
          <span className="truncate font-heading text-sm font-bold text-heading">
            {profile.greeting}
          </span>
          <span className="truncate text-xs text-muted">{t('levels.named', { name: levelName(profile.cefr) })}</span>
        </div>
      </div>

      <nav
        className="hidden shrink-0 items-center gap-1 rounded-full bg-sand/60 p-1 md:flex"
        aria-label={t('nav.main')}
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            aria-label={t(`nav.${item.labelKey}`)}
            title={t(`nav.${item.labelKey}`)}
            className={({ isActive }) => tabClass(isActive)}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="header-tab"
                    className="absolute inset-0 rounded-full bg-surface shadow-soft ring-1 ring-line"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <item.icon size={18} stroke={2} aria-hidden="true" className={`relative ${item.primary === true ? 'text-accent-dark' : ''}`} />
                {/* На планшете — только иконки: пять подписей не влезают
                    рядом с логотипом и счётчиками. */}
                <span className="relative hidden lg:inline">{t(`nav.${item.labelKey}`)}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
        <div className="hidden min-w-0 lg:flex">
          <StatusChips />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle />
          <SoundToggle />
        </div>
      </div>
    </header>
  );
}
